/**
 * Drupal Entity Edit Button
 * Opens Drupal edit interface in a new window
 * Toggle visibility with Ctrl+Alt+A (persisted in cookie)
 */

'use client';

import { Edit3 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

type EntityKind = 'node' | 'term';

export interface DrupalEditButtonProps {
  /** Drupal entity type */
  entity: EntityKind;
  /** Numeric internal id (nid for node, tid for term). Preferred when available. */
  internalId?: number | string;
  /** Optional override to pass a complete edit URL directly. */
  urlOverride?: string;
  /** Tooltip message. */
  message?: string;
  /** Always show the button regardless of edit mode */
  alwaysShow?: boolean;
}

function getDrupalBaseUrl(): string {
  const base = (process.env.NEXT_PUBLIC_DRUPAL_BASE_URL || process.env.DRUPAL_URL || '').trim();
  return base.replace(/\/$/, '');
}

function buildEditUrl(params: {
  entity: EntityKind;
  internalId?: number | string;
  urlOverride?: string;
}): string | null {
  const { entity, internalId, urlOverride } = params;
  if (urlOverride) return urlOverride;
  const base = getDrupalBaseUrl();
  if (!base) return null;
  if (!internalId) return null;
  if (entity === 'node') return `${base}/node/${internalId}/edit`;
  if (entity === 'term') return `${base}/taxonomy/term/${internalId}/edit`;
  return null;
}

/**
 * Hook to manage edit mode visibility via cookie
 * Toggle with Ctrl+Alt+A keyboard shortcut
 */
const useEditMode = () => {
  const [isEditModeVisible, setIsEditModeVisible] = useState(false);

  useEffect(() => {
    const editModeCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('editMode='))
      ?.split('=')[1];
    setIsEditModeVisible(editModeCookie === 'true');

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.altKey && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        setIsEditModeVisible(currentState => {
          const newState = !currentState;
          const expires = new Date();
          expires.setDate(expires.getDate() + 30);
          document.cookie = `editMode=${newState}; expires=${expires.toUTCString()}; path=/`;
          return newState;
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return isEditModeVisible;
};

/**
 * Drupal Entity Edit Button
 * 
 * Features:
 * - Toggle visibility with Ctrl+Alt+A (persisted in cookie)
 * - Opens edit page in new window
 * - Supports custom URLs via urlOverride
 * - Can be forced visible with alwaysShow
 * 
 * @example
 * ```tsx
 * <DrupalEditButton
 *   entity="node"
 *   internalId={123}
 *   message="Edit event in Drupal"
 * />
 * ```
 */
export function DrupalEditButton({
  entity,
  internalId,
  urlOverride,
  message,
  alwaysShow = false,
}: DrupalEditButtonProps) {
  const isEditModeVisible = useEditMode();
  const url = buildEditUrl({ entity, internalId, urlOverride });

  const handleClick = useCallback(() => {
    if (url) window.open(url, '_blank');
  }, [url]);

  if ((!isEditModeVisible || !url) && !alwaysShow) return null;

  return (
    <div className="relative inline-flex group">
      <button
        onClick={handleClick}
        className="inline-flex items-center justify-center rounded-full bg-primary p-2 text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label="Edit"
        type="button"
      >
        <Edit3 className="h-4 w-4" />
      </button>
      
      {/* Tooltip */}
      <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-popover px-3 py-2 text-sm text-popover-foreground shadow-md opacity-0 transition-opacity group-hover:opacity-100">
        <div className="font-semibold">{message || 'Edit in Drupal'}</div>
        <div className="text-xs text-muted-foreground">{url}</div>
        {/* Arrow */}
        <div className="absolute left-1/2 top-full -translate-x-1/2 -translate-y-1/2 border-4 border-transparent border-t-popover" />
      </div>
    </div>
  );
}

/**
 * Extract node ID from event for Drupal edit button
 * Handles various ID formats from GraphQL
 * 
 * @param event - Event object with potential ID fields
 * @returns Numeric node ID or undefined
 */
export function extractNodeId(event: {
  eventExtranetNid?: number | string | null;
  id?: string | null;
}): number | string | undefined {
  // Prefer eventExtranetNid if available
  if (event.eventExtranetNid) {
    return event.eventExtranetNid;
  }

  if (event.id) {
    // Try to extract from "node:123" format
    const match = event.id.match(/node:(\d+)/);
    if (match) {
      return parseInt(match[1], 10);
    }

    // If it's already a number string
    if (/^\d+$/.test(event.id)) {
      return parseInt(event.id, 10);
    }
  }

  return undefined;
}

/**
 * Default Drupal Edit Button for events
 * Pre-configured for event nodes
 * 
 * @example
 * ```tsx
 * <DefaultEventEditButton event={event} />
 * ```
 */
export function DefaultEventEditButton({
  event,
  alwaysShow = false,
}: {
  event: { eventExtranetNid?: number | string | null; id?: string | null };
  alwaysShow?: boolean;
}) {
  const nodeId = extractNodeId(event);

  if (!nodeId) return null;

  return (
    <DrupalEditButton
      entity="node"
      internalId={nodeId}
      message="Edit event in Drupal"
      alwaysShow={alwaysShow}
    />
  );
}

